import type {
  AvailabilityError,
  AvailabilityErrorCode,
  AvailabilityErrorDetails
} from "@gettactic/api";

export type SchedulableNonAvailableEntry = {
  reason_code: AvailabilityErrorCode;
  details?: AvailabilityErrorDetails;
};

// The schedulable endpoint only reports availability per resource, but these
// two codes describe the office itself (closed hours, holidays, booking
// horizon), so they are the ones worth showing before a resource is picked.
const officeLevelErrorCodes: AvailabilityErrorCode[] = [
  "office_not_available",
  "office_future_limit"
];

const detailsKey = (details: AvailabilityErrorDetails | undefined): string => {
  if (!details) {
    return "";
  }
  const sortedEntries = Object.entries(details).sort(([a], [b]) =>
    a.localeCompare(b)
  );
  return JSON.stringify(sortedEntries);
};

/**
 * Collapses the per-resource `non_available` entries of one or more
 * schedulable responses into the distinct office-level reasons, in first-seen
 * order. Every desk in a closed office repeats the same closure, so a
 * multi-day selection would otherwise show one alert per desk per day.
 */
export const deriveOfficeAvailabilityErrors = (
  nonAvailable: SchedulableNonAvailableEntry[]
): AvailabilityError[] => {
  const seen = new Set<string>();
  const errors: AvailabilityError[] = [];
  for (const entry of nonAvailable) {
    if (!officeLevelErrorCodes.includes(entry.reason_code)) {
      continue;
    }
    const key = `${entry.reason_code}:${detailsKey(entry.details)}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    errors.push({ reasonCode: entry.reason_code, details: entry.details });
  }
  return errors;
};
